import { defineStore } from 'pinia'
import {
  PriceInformation,
  PriceInformationPostIn,
  SoftwareLicensesService,
} from '@/client'

import { useApplicationDetailsStore } from '@/stores/ApplicationDetailsView.store'
import { sendToast } from '@/components/sm/SmNotification'
import i18n from '@/i18n/i18n'

export const useContractStore = defineStore('contractStore', () => {
  type LicenseId = string
  const contractCache = ref<{ [key: LicenseId]: PriceInformation[] }>({})
  const editStatus = ref<{ [key: string]: boolean }>({})

  const lastChangedTime = ref<string>(new Date().toISOString())

  async function loadContract(licenseId: LicenseId) {
    await SoftwareLicensesService.getSoftwareLicensePriceInformationsForSoftwareLicenseApiV1SoftwareSoftwareLicensesSoftwareLicenseIdPriceInformationsGet(
      { softwareLicenseId: licenseId }
    )
      .then((res) => {
        contractCache.value[licenseId] = res
      })
      .catch(() => {
        sendToast(
          i18n.global.t('licenseModelDeleteFailed.title'),
          i18n.global.t(
            'dialogs.licenceModelEditDialog.licenseModelForm.alerts.loadedPriceModelError'
          ),
          'error'
        )
      })
  }

  async function loadContracts(applicationId: string) {
    await SoftwareLicensesService.getSoftwareLicensePriceInformationsApiV1SoftwareSoftwareLicensePriceInformationsGet(
      { softwareId: applicationId }
    )
      .then((res) => {
        const nextCache: Record<string, PriceInformation[]> = {}

        res.forEach((contract: PriceInformation) => {
          const licenseId = contract.software_license_id

          if (!nextCache[licenseId]) {
            nextCache[licenseId] = []
          }

          nextCache[licenseId].push(contract)
        })

        // Replace cached contracts for the fetched licenses to avoid duplicates
        contractCache.value = {
          ...contractCache.value,
          ...nextCache,
        }
      })
      .catch(() => {
        sendToast(
          i18n.global.t('licenseModelDeleteFailed.title'),
          i18n.global.t(
            'dialogs.licenceModelEditDialog.licenseModelForm.alerts.loadedPriceModelError'
          ),
          'error'
        )
      })
  }

  async function createContract(
    licenseId: LicenseId,
    contract: PriceInformationPostIn
  ) {
    return await SoftwareLicensesService.createSoftwareLicensePriceInformationApiV1SoftwareSoftwareLicensesSoftwareLicenseIdPriceInformationsPost(
      {
        softwareLicenseId: licenseId,
        requestBody: contract,
      }
    )
      .then((res) => {
        const applicationDetailsViewStore = useApplicationDetailsStore()

        if (!contractCache.value[licenseId]) {
          contractCache.value[licenseId] = []
        }

        contractCache.value[licenseId].push(res)

        applicationDetailsViewStore.loadQuota(licenseId)
        lastChangedTime.value = new Date().toISOString()

        return res
      })
      .catch(() => {
        sendToast(
          i18n.global.t('licenseModelDeleteFailed.title'),
          i18n.global.t(
            'dialogs.licenceModelEditDialog.licenseModelForm.alerts.createdPriceModelError'
          ),
          'error'
        )
      })
  }

  async function updateContract(
    licenseModelId: string,
    contract: PriceInformation
  ) {
    return await SoftwareLicensesService.updateSoftwareLicensePriceInformationApiV1SoftwareSoftwareLicensePriceInformationsPriceInformationIdPatch(
      {
        priceInformationId: contract._id,
        requestBody: contract,
      }
    )
      .then((updatedContract) => {
        const applicationDetailsViewStore = useApplicationDetailsStore()

        if (contractCache.value[licenseModelId]) {
          contractCache.value[licenseModelId] = contractCache.value[
            licenseModelId
          ].map((item) => {
            if (item._id === contract._id) {
              return contract
            }
            return item
          })
        }

        applicationDetailsViewStore.loadQuota(licenseModelId)
        lastChangedTime.value = new Date().toISOString()

        return updatedContract
      })
      .catch(() => {
        sendToast(
          i18n.global.t('licenseModelDeleteFailed.title'),
          i18n.global.t(
            'dialogs.licenceModelEditDialog.licenseModelForm.alerts.savePriceModelError'
          ),
          'error'
        )
      })
  }

  async function deleteContract(licenseModelId: string, contractId: string) {
    await SoftwareLicensesService.deleteSoftwareLicensePriceInformationApiV1SoftwareSoftwareLicensePriceInformationsPriceInformationIdDelete(
      {
        priceInformationId: contractId,
      }
    )
      .then(() => {
        const applicationDetailsViewStore = useApplicationDetailsStore()

        if (contractCache.value[licenseModelId]) {
          contractCache.value[licenseModelId] = contractCache.value[
            licenseModelId
          ].filter((item) => item._id !== contractId)
        }

        applicationDetailsViewStore.loadQuota(licenseModelId)
        lastChangedTime.value = new Date().toISOString()

        sendToast(
          i18n.global.t(
            'dialogs.licenceModelEditDialog.licenseModelForm.alerts.deletePriceModel'
          ),
          undefined,
          'success'
        )
      })
      .catch(() => {
        sendToast(
          i18n.global.t('licenseModelDeleteFailed.title'),
          i18n.global.t(
            'dialogs.licenceModelEditDialog.licenseModelForm.alerts.deletePriceModelError'
          ),
          'error'
        )
      })
  }

  function getContract(licenseId: LicenseId) {
    if (contractCache.value[licenseId]) {
      const cache: PriceInformation[] = contractCache.value[licenseId]
      return cache
    }
  }

  function hasMultipleContracts(id: LicenseId) {
    return contractCache.value[id].length > 1
  }

  return {
    contractCache,
    editStatus,
    lastChangedTime,
    loadContract,
    loadContracts,
    createContract,
    updateContract,
    deleteContract,
    getContract,
    hasMultipleContracts,
  }
})
